import type { AccountingStatus, IExpenseReport } from '../Expenses';
import { COST_CENTERS, ORG_SECTIONS } from '../../../../constants/orgStructure';

// Status que entram no controle de KM. RECUSADO nunca entra.
export type KmStatus = Exclude<AccountingStatus, 'RECUSADO'>;

export const KM_STATUS_OPTIONS: { value: KmStatus; label: string }[] = [
  { value: 'EM PROCESSO', label: 'Em processo' },
  { value: 'AGUARDANDO LANCAMENTO', label: 'Aguardando lançamento' },
  { value: 'LANCADO', label: 'Lançado' },
];

// Um deslocamento = um item "Quilometragem adicional" de um relatório
export interface IKmEntry {
  id: string;
  report: IExpenseReport;
  date: string; // YYYY-MM-DD
  userId: string;
  userName: string;
  section: string;
  sectionLabel: string;
  costCenter: string;
  costCenterLabel: string;
  kmStart?: number;
  kmEnd?: number;
  km: number;
  reason: string;
  amount: number;
  status: KmStatus;
}

export interface IKmFilters {
  dateFrom: string;
  dateTo: string;
  userId: string;
  section: string;
  costCenter: string;
  statuses: KmStatus[];
}

const toNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const getSectionLabel = (sector: string, section: string): string => {
  if (!section) return 'Sem seção';
  const found = ORG_SECTIONS.find(
    s => s.code === section && s.sector === sector,
  );
  return found ? `${found.code} - ${found.name}` : section;
};

const getCostCenterLabel = (costCenter: string): string => {
  if (!costCenter) return 'Sem centro de custo';
  const found = COST_CENTERS.find(c => c.code === costCenter);
  return found ? `${found.code} - ${found.name}` : costCenter;
};

/**
 * Monta a lista de deslocamentos a partir dos relatórios.
 *
 * Regras:
 *  - só itens do tipo "Quilometragem adicional";
 *  - relatórios RECUSADOS ficam de fora;
 *  - relatórios que foram consolidados (filhos) ficam de fora, porque o
 *    consolidado copia os itens deles — senão o KM seria contado duas vezes;
 *  - seção e centro de custo são os do responsável pelo relatório (mesmo
 *    quando há rateio).
 */
export function buildKmEntries(
  reports: IExpenseReport[],
  getUserName: (id: string) => string,
): IKmEntry[] {
  const entries: IKmEntry[] = [];

  reports.forEach(report => {
    const status = (report.status || 'EM PROCESSO') as AccountingStatus;
    if (status === 'RECUSADO') return;
    if (report.isConsolidated || report.is_consolidated) return;

    const userId = report.director || (report as any).user_id || '';
    const userName =
      report.director_name || report.user?.name || getUserName(userId);
    const sector = String(report.sector ?? '');
    const section = String(report.section ?? '');
    const costCenter = String(report.cost_center ?? '');

    (report.items || []).forEach((item, index) => {
      if (item.expense_type !== 'Quilometragem adicional') return;

      const kmStart = toNumber(item.km_start);
      const kmEnd = toNumber(item.km_end);
      const km =
        toNumber(item.additional_km) ??
        (kmStart !== undefined && kmEnd !== undefined ? kmEnd - kmStart : 0);

      entries.push({
        id: item.id || `${report.id}-${index}`,
        report,
        date: (report.date || '').substring(0, 10),
        userId,
        userName,
        section,
        sectionLabel: getSectionLabel(sector, section),
        costCenter,
        costCenterLabel: getCostCenterLabel(costCenter),
        kmStart,
        kmEnd,
        km,
        reason: item.displacement_reason || '',
        amount: Number(item.amount) || 0,
        status: status as KmStatus,
      });
    });
  });

  return entries.sort((a, b) => b.date.localeCompare(a.date));
}

export function filterKmEntries(
  entries: IKmEntry[],
  filters: IKmFilters,
): IKmEntry[] {
  return entries.filter(entry => {
    if (filters.dateFrom && entry.date < filters.dateFrom) return false;
    if (filters.dateTo && entry.date > filters.dateTo) return false;
    if (filters.userId && entry.userId !== filters.userId) return false;
    if (filters.section && entry.sectionLabel !== filters.section) return false;
    if (filters.costCenter && entry.costCenterLabel !== filters.costCenter) {
      return false;
    }
    return filters.statuses.includes(entry.status);
  });
}

export interface IKmGroup {
  label: string;
  km: number;
}

// Soma o KM por um campo e devolve do maior para o menor
export function sumKmBy(
  entries: IKmEntry[],
  getKey: (entry: IKmEntry) => string,
): IKmGroup[] {
  const totals = new Map<string, number>();
  entries.forEach(entry => {
    const key = getKey(entry);
    totals.set(key, (totals.get(key) || 0) + entry.km);
  });
  return Array.from(totals, ([label, km]) => ({ label, km })).sort(
    (a, b) => b.km - a.km,
  );
}

// Opções únicas (ordenadas) para os selects de filtro
export function uniqueOptions(
  entries: IKmEntry[],
  getKey: (entry: IKmEntry) => string,
): string[] {
  return Array.from(new Set(entries.map(getKey))).sort((a, b) =>
    a.localeCompare(b, 'pt-BR'),
  );
}

export const formatKm = (value: number): string =>
  `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;

export const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// YYYY-MM-DD -> DD/MM/YYYY
export const formatDate = (date: string): string => {
  const [year, month, day] = date.split('-');
  return year && month && day ? `${day}/${month}/${year}` : '—';
};

const STATUS_LABEL: Record<KmStatus, string> = {
  'EM PROCESSO': 'Em processo',
  'AGUARDANDO LANCAMENTO': 'Aguardando lançamento',
  LANCADO: 'Lançado',
};

export const getStatusLabel = (status: KmStatus): string =>
  STATUS_LABEL[status] || status;

// Gera um CSV (separado por ";", com BOM) que abre direto no Excel em pt-BR
export function downloadKmCsv(entries: IKmEntry[], filename: string): void {
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const number = (value?: number) =>
    value === undefined ? '' : String(value).replace('.', ',');

  const header = [
    'Data',
    'Usuário',
    'Seção',
    'Centro de custo',
    'KM inicial',
    'KM final',
    'KM percorrido',
    'Motivo',
    'Valor (R$)',
    'Status',
  ];

  const rows = entries.map(entry => [
    formatDate(entry.date),
    escape(entry.userName),
    escape(entry.sectionLabel),
    escape(entry.costCenterLabel),
    number(entry.kmStart),
    number(entry.kmEnd),
    number(entry.km),
    escape(entry.reason),
    entry.amount.toFixed(2).replace('.', ','),
    getStatusLabel(entry.status),
  ]);

  const csv = [header, ...rows].map(row => row.join(';')).join('\r\n');
  // BOM: faz o Excel reconhecer os acentos (UTF-8)
  const bom = String.fromCharCode(0xfeff);
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
